import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Copy, Hammer } from 'lucide-react';
import type { OnePager } from '../types';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { htmlToMarkdown } from '../lib/markdown-utils';
import { apiService } from '../services/ApiService';

interface OnePagerListingProps {
  onePagers: OnePager[];
  onView: (id: string) => void;
  onCreateFromTemplate: (onePager: OnePager) => void;
  onCreateNew: () => void;
  loading?: boolean;
  searchTerm?: string;
  usernameFilters?: string[];
}

export const OnePagerListing: React.FC<OnePagerListingProps> = ({
  onePagers,
  onView,
  onCreateFromTemplate,
  onCreateNew,
  loading = false,
  searchTerm = '',
  usernameFilters = []
}) => {
  const navigate = useNavigate();

  const renderPreviewHtml = (onePager: OnePager) => {
    if (onePager.content && onePager.content.trim()) {
      return onePager.content;
    }
    return '<p class="text-gray-400 italic">No content available</p>';
  };

  const filteredOnePagers = onePagers.filter(onePager => {
    const matchesSearch =
      onePager.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (onePager.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    if (usernameFilters.length === 0) return matchesSearch;
    const creatorUsername = onePager.creator?.username?.toLowerCase();
    return (
      matchesSearch &&
      creatorUsername &&
      usernameFilters.some(u => u.toLowerCase() === creatorUsername)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-600">Loading one-pagers...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Grid */}
        {filteredOnePagers.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {/* Hero section*/}
            <div
                key="hero"
                className="flex flex-col gap-6 h-132 w-full sm:w-full md:max-w-96 transition-all group bg-white">
              <div className="flex text-5xl font-extrabold text-gray-400 items-center">
                Describe your app in a page, or remix existing ones for
              </div>
              <div>
                <ul className="space-y-4">
                  <li className="flex text-3xl font-bold text-gray-600 items-center">
                    LLM generation
                  </li>
                  <li className="flex text-3xl font-bold text-gray-600 items-center gap-2">
                    Lesser Hallucinations
                  </li>
                  <li className="flex text-3xl font-bold text-gray-600 items-center">
                    Reliable Output
                  </li>
                  <li className="flex text-3xl font-bold text-gray-600 items-center gap-2">
                    Smaller Bills
                  </li>
                </ul>
              </div>
              <div className="flex gap-4">
                <Button
                  size="lg"
                  className="h-12 text-sm font-normal rounded-sm text-white bg-gray-900 hover:bg-gray-800 shadow-sm hover:shadow-md"
                  onClick={onCreateNew}
                >
                  New One-Pager
                </Button>
                <Button
                  size="lg"
                  className="h-12 text-sm font-normal rounded-sm border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md"
                  onClick={() => navigate('/signup')}
                >
                  Sign up
                </Button>
              </div>
            </div>
            {filteredOnePagers.map((onePager) => (
              <Card
                key={onePager.id}
                className="border-gray-200 w-full sm:w-full md:max-w-96 hover:border-gray-300 hover:shadow-md transition-all cursor-pointer group bg-white shadow-sm rounded-sm"
                onClick={() => onView(onePager.id)}
              >
                {/* Portrait mini preview - 70% of height */}
                <div className="w-full h-96 p-5 overflow-hidden bg-gray-50 border-b border-gray-200 relative">
                  {/* Paper-like styling */}
                  <div className="absolute inset-0 bg-gradient-to-b from-white to-gray-50 opacity-30"></div>
                  <div className="relative z-10 prose prose-sm max-w-none text-gray-900 scale-50 origin-top-left leading-7 [&_h1]:text-4xl [&_h1]:font-bold [&_h1]:leading-tight [&_h1]:my-8 [&_h1]:text-gray-900 [&_h2]:text-3xl [&_h2]:font-semibold [&_h2]:leading-snug [&_h2]:mt-6 [&_h2]:mb-3 [&_h2]:text-gray-900 [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:leading-normal [&_h3]:mt-5 [&_h3]:mb-2 [&_h3]:text-gray-900 [&_p]:my-3 [&_p]:leading-7 [&_ul]:my-3 [&_ul]:pl-6 [&_ul]:list-disc [&_ol]:my-3 [&_ol]:pl-6 [&_ol]:list-decimal [&_li]:my-1 [&_li]:leading-relaxed [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:my-4 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_pre]:bg-gray-100 [&_pre]:rounded-lg [&_pre]:p-4 [&_pre]:my-4 [&_pre]:overflow-x-auto [&_pre]:font-mono [&_pre]:text-sm [&_pre]:leading-relaxed [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:font-mono [&_code]:text-sm" style={{ height: '200%', overflow: 'hidden', width: '200%' }}
                    dangerouslySetInnerHTML={{ __html: renderPreviewHtml(onePager) }}
                  />
                  {/* Paper shadow effect */}
                  <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-gray-200 to-transparent opacity-30"></div>
                </div>

                {/* Description and buttons - 30% of height */}
                <div className="flex flex-col p-4 bg-white h-36">
                  <div className="mb-2">
                    {onePager.creator && (
                      <div className="flex items-center gap-2 mb-2">
                        <p 
                          className="text-xs text-gray-900 font-semibold hover:text-blue-600 cursor-pointer transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onePager.creator?.username) {
                              navigate(`/u/${onePager.creator.username}`);
                            }
                          }}
                        >
                          @{onePager.creator.username}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <div className="flex items-center gap-1">
                            <Copy className="w-3 h-3" />
                            <span>{onePager.clone_count || 0}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Hammer className="w-3 h-3" />
                            <span>{onePager.build_count || 0}</span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="h-8 mb-2">
                      {onePager.description ? (
                        <p className="text-xs text-gray-600 leading-relaxed line-clamp-2">
                          {onePager.description}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-400 italic leading-relaxed line-clamp-2">
                          No description available
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 mt-auto">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await apiService.incrementCloneCount(onePager.id);
                        } catch (error) {
                          console.error('Failed to track clone:', error);
                        }
                        onCreateFromTemplate(onePager);
                      }}
                      className="flex-1 h-8 text-xs font-normal border-gray-200 hover:border-gray-300 rounded-sm transform hover:scale-105 transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      <Copy className="w-3 h-3 mr-1" /> Clone
                    </Button>
                    <Button
                      size="sm"
                      className="relative flex-1 h-8 text-xs font-normal rounded-sm text-gray-900 bg-white transform hover:scale-105 transition-all duration-200 shadow-sm hover:shadow-md before:absolute before:inset-0 before:rounded-sm before:p-[2px] before:bg-gradient-to-r before:from-purple-500 before:via-blue-500 before:to-cyan-500 hover:before:from-purple-600 hover:before:via-blue-600 hover:before:to-cyan-600 before:-z-10 after:absolute after:inset-[2px] after:rounded-[calc(0.125rem-2px)] after:bg-white after:-z-10"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await apiService.incrementBuildCount(onePager.id);
                        } catch (error) {
                          console.error('Failed to track build:', error);
                        }
                        const markdownContent = htmlToMarkdown(onePager.content || '');
                        const fullPrompt = `${onePager.title || ''}\n\n${onePager.description || ''}\n\n${markdownContent}`;
                        const lovableUrl = `https://lovable.dev/?autosubmit=true#prompt=${encodeURIComponent(fullPrompt)}`;
                        window.open(lovableUrl, '_blank');
                      }}
                    >
                      <Hammer className="w-3 h-3 mr-1" />
                      Build
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-500">{searchTerm ? 'No one-pagers match your search.' : 'No one-pagers available yet.'}</div>
            {!searchTerm && (
              <Button onClick={onCreateNew} variant="outline" className="mt-4 border-gray-200 hover:border-gray-300">
                <Plus className="w-4 h-4 mr-2" /> Create the first one-pager
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
